import React, { useState, useEffect } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  IonPage,
  IonContent,
} from "@ionic/react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Divider,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Dialog,
  DialogTitle as MuiDialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme as useMuiTheme,
  styled,
  Link as MuiLink,
  createTheme,
} from "@mui/material";
import {
  People,
  School,
  HowToReg,
  EventAvailable,
  LocationCity,
  Refresh,
  Dashboard,
  Settings,
  LockReset,
  Menu as MenuIcon,
  ArrowBack,
  Logout,
  Search,
  GetApp,
  PictureAsPdf,
  FilterList,
  Visibility,
  Assessment,
  Close as CloseIcon,
  Person,
  TrendingUp,
  CheckCircle,
  Male,
  Female,
  ChildCare,
  Group,
  AdminPanelSettings,
  Domain,
  AccountTree,
  FileUpload,
} from "@mui/icons-material";
import { ThemeProvider } from "@emotion/react";
import axios from "axios";
import { baseUrl, requestID, config, logOutUser, getUser } from "../../common/utils";
import { useHistory, useLocation } from "react-router-dom";
import SchoolActivityStats from "./SchoolActivityStats";

// ============================================
// Modern Dashboard Theme (MUI5) - Matching DeltaGovPortal
// ============================================
const theme = createTheme({
  palette: {
    primary: {
      main: "#243b5a",
    },
    secondary: {
      main: "#ef4444",
    },
    success: {
      main: "#059669",
    },
    error: {
      main: "#dc2626",
    },
    info: {
      main: "#3b82f6",
    },
    warning: {
      main: "#f59e0b",
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontSize: "1.5rem",
      fontWeight: 700,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.875rem",
      color: "#6b7280",
    },
    body2: {
      fontSize: "0.875rem",
      color: "#374151",
    },
    caption: {
      fontSize: "0.75rem",
      letterSpacing: "1px",
      color: "#6b7280",
      textTransform: "uppercase",
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "#ffffff",
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          borderRadius: "6px",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
            borderColor: "#243b5a",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          padding: "6px 16px",
          borderRadius: 6,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: "#f8f9fa",
          fontSize: "13px",
          color: "#4b5563",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#d1d5db",
          borderWidth: "1px",
        },
      },
    },
  },
});

// ============================================
// Styled Components - Matching ValidationListPage
// ============================================
const Header = styled(Box)(({ theme }) => ({
  backgroundColor: "#243b5a",
  padding: "16px 40px",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
}));

const HeaderTopRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const HeaderBottomRow = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
  marginTop: "8px",
});

const LogoCircle = styled(Box)({
  width: "55px",
  height: "55px",
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  color: "#243b5a",
  fontWeight: "bold",
  border: "2px solid #f0c45a",
  marginRight: "16px",
});

const FooterLink = styled(MuiLink)({
  color: "#d1d5db",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    color: "#ffffff",
  },
});

const SchoolInfoBox = styled(Box)({
  textAlign: "right",
  color: "#f1f5f9",
});

const LogoutButton = styled(Button)({
  backgroundColor: "#ef4444",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: "#dc2626",
  },
  "& svg": {
    marginRight: "8px",
  },
});

// Navigation Menu Styled Components
const NavMenu = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "12px",
  flexWrap: "wrap",
});

const NavButton = styled(Button)<{ active?: boolean }>(({ active }) => ({
  color: active ? "#ffffff" : "#e0e7ff",
  backgroundColor: active ? "rgba(255,255,255,0.15)" : "transparent",
  fontWeight: active ? 600 : 500,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "20px",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "#ffffff",
  },
  "& svg": {
    marginRight: "8px",
    fontSize: "18px",
  },
}));

// Stats Cards styled to match ValidationListPage
const StatsGrid = styled(Grid)({
  marginBottom: "32px",
});

const StatCard = styled(Paper)({
  padding: "20px 24px",
  height: "100%",
  backgroundColor: "#ffffff",
});

const StatLabel = styled(Typography)({
  fontSize: "14px",
  color: "#6b7280",
  marginBottom: "8px",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  fontWeight: 500,
});

const StatValue = styled(Typography)({
  fontSize: "32px",
  fontWeight: 700,
  color: "#1f2937",
  lineHeight: 1.2,
});

const StatusBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const StatusActive = styled(Typography)({
  fontSize: "20px",
  fontWeight: 600,
  color: "#10b981",
  lineHeight: 1.2,
});

const StatusOnline = styled(Typography)({
  fontSize: "14px",
  color: "#6b7280",
});

// Filter bar styled to match ValidationListPage
const FilterBar = styled(Paper)({
  padding: "16px 20px",
  marginBottom: "24px",
  display: "flex",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
});

const SearchField = styled(TextField)({
  flex: 2,
  minWidth: "300px",
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
  },
});

const FilterSelect = styled(FormControl)({
  minWidth: '120px',
  backgroundColor: '#ffffff',
});

const ActionButton = styled(Button)({
  border: "1px solid #e5e7eb",
  color: "#374151",
  backgroundColor: "#ffffff",
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: "#f9fafb",
    borderColor: "#d1d5db",
  },
});

const IconActionButton = styled(IconButton)({
  border: "1px solid #e5e7eb",
  borderRadius: "6px",
  padding: "8px",
  backgroundColor: "#ffffff",
  '&:hover': {
    backgroundColor: "#f9fafb",
  },
});

// Table styles to match ValidationListPage
const StyledTableContainer = styled(TableContainer)({
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  overflow: "hidden",
  marginTop: "16px",
});

const StyledTableHead = styled(TableHead)({
  backgroundColor: "#f8f9fa",
  '& .MuiTableCell-head': {
    fontWeight: 600,
    fontSize: "13px",
    color: "#4b5563",
    padding: "12px 16px",
    borderBottom: "1px solid #e5e7eb",
  },
});

const StyledTableCell = styled(TableCell)({
  padding: "12px 16px",
  fontSize: "14px",
  borderBottom: "1px solid #e5e7eb",
});

const StyledTableRow = styled(TableRow)({
  '&:hover': {
    backgroundColor: "#f9fafb",
  },
});

const ViewButton = styled(Button)({
  backgroundColor: "#243b5a",
  color: "white",
  padding: "4px 12px",
  fontSize: "12px",
  minWidth: "60px",
  "&:hover": {
    backgroundColor: "#162c47",
  },
  "& svg": {
    fontSize: "16px",
    marginRight: "4px",
  },
});

// Dialog specific styled components
const DialogHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 24px",
  borderBottom: "1px solid #e5e7eb",
});

const DialogTitleStyled = styled(Typography)({
  fontSize: "1.25rem",
  fontWeight: 600,
  color: "#243b5a",
});

const SchoolStatsGrid = styled(Grid)({
  marginBottom: "24px",
});

const SchoolStatCard = styled(Paper)({
  padding: "16px",
  textAlign: "center",
  height: "100%",
  backgroundColor: "#f8fafc",
});

const SchoolStatValue = styled(Typography)({
  fontSize: "24px",
  fontWeight: 700,
  color: "#243b5a",
  lineHeight: 1.2,
});

const SchoolStatLabel = styled(Typography)({
  fontSize: "12px",
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginTop: "4px",
});

// ============================================
// Types - Updated to match actual API responses
// ============================================
interface LGAStat {
  lgaName: string;
  lgaCode?: string; // Added lgaCode field
  studentCount: number;
  schoolCount: number;
}

interface DashboardData {
  totalStudents: string | number;
  totalSchools: string | number;
  registrationsToday: string | number;
  registrationStatus: string | number;
  studentsByLGA: LGAStat[] | null;
}

// School details interface matching actual API response from your sample
interface SchoolDetail {
  id: number;
  schoolCode: string | number;
  schoolName: string;
  totalStudents: number;
  totalMale: string | number;
  totalFemale: string | number;
}

interface SchoolsByLGAResponse {
  status: boolean;
  message: string;
  data: SchoolDetail[]; // Direct array of schools based on your sample
}

// ============================================
// Placeholder Components for Navigation
// ============================================
const StudentsView: React.FC = () => (
  <Box sx={{ p: 3, textAlign: 'center' }}>
    <Typography variant="h5" gutterBottom>Students View Component</Typography>
    <Typography color="text.secondary">This is where the students list will be displayed.</Typography>
  </Box>
);

const SchoolsView: React.FC = () => (
  <Box sx={{ p: 3, textAlign: 'center' }}>
    <Typography variant="h5" gutterBottom>Schools View Component</Typography>
    <Typography color="text.secondary">This is where the schools list will be displayed.</Typography>
  </Box>
);

const SettingsView: React.FC = () => (
  <Box sx={{ p: 3, textAlign: 'center' }}>
    <Typography variant="h5" gutterBottom>Settings Component</Typography>
    <Typography color="text.secondary">System configuration and preferences.</Typography>
  </Box>
);

const ManagementConsoleView: React.FC = () => (
  <Box sx={{ p: 3, textAlign: 'center' }}>
    <Typography variant="h5" gutterBottom>Management Console</Typography>
    <Typography color="text.secondary">Advanced management and control panel.</Typography>
  </Box>
);

const DomainOneView: React.FC = () => (
  <Box sx={{ p: 3, textAlign: 'center' }}>
    <Typography variant="h5" gutterBottom>Domain One</Typography>
    <Typography color="text.secondary">First additional domain component.</Typography>
  </Box>
);

const DomainTwoView: React.FC = () => (
  <Box sx={{ p: 3, textAlign: 'center' }}>
    <Typography variant="h5" gutterBottom>Domain Two</Typography>
    <Typography color="text.secondary">Second additional domain component.</Typography>
  </Box>
);

// ============================================
// Main AdminDashboard Component
// ============================================
const AdminDashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [systemSettings, setSystemSettings] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [lgaFilter, setLgaFilter] = useState("All LGAs");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [message, setMessage] = useState<{ type: 'success' | 'error' | '', text: string }>({ type: '', text: '' });

  // Navigation state
  const [activeMenu, setActiveMenu] = useState<string>("dashboard");

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLGA, setSelectedLGA] = useState<{ name: string; code: string }>({ name: "", code: "" });
  const [schoolDetails, setSchoolDetails] = useState<SchoolDetail[]>([]);
  const [schoolsSummary, setSchoolsSummary] = useState<{
    totalSchools: number;
    totalStudents: number;
    totalMale: number;
    totalFemale: number;
  }>({
    totalSchools: 0,
    totalStudents: 0,
    totalMale: 0,
    totalFemale: 0,
  });
  const [schoolsLoading, setSchoolsLoading] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // Get user data from utils
  const user = getUser();

  const userData = {
    name: user?.name || "ADMIN USER",
    lgaCode: user?.lgaCode || "",
    schoolCode: user?.schoolCode || "",
  };

  // Fetch system settings from getSettings endpoint
  const fetchSettings = async () => {
    try {
      const response = await axios.post(`${baseUrl()}Admin2s/getSettings`, {});
      if (response.data.status && response.data.data) {
        setSystemSettings(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  useEffect(() => {
    fetchDashboardInfo();
    fetchSettings();
  }, []);

  const fetchDashboardInfo = async () => {
    setLoading(true);
    try {
      /*
      // =====================================================================
      // OLD WAY (For Powerful Servers)
      // To revert to the former way of fetching full dashboard info,
      // uncomment the block below and remove the TEMPORARY NEW WAY block.
      // See REVERT_DASHBOARD_DATA_FETCH.md for full details.
      // =====================================================================
      const response = await axios.post(`${baseUrl()}Admin2s/getDashboardInfo`);
      console.log(response.data);
      if (response.data.status) {
        setData(response.data.data);
        setMessage({ type: "success", text: "Dashboard data loaded successfully" });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
      */

      // =====================================================================
      // TEMPORARY NEW WAY (For current server limits)
      // Uses getLgaReport to build the dashboard UI without overloading the server.
      // =====================================================================
      const response = await axios.post(`${baseUrl()}Admin2s/getLgaReport`);
      console.log("Temporary getLgaReport response:", response.data);
      if (response.data.status && response.data.data) {
        const reportData = response.data.data;

        let totalStudents = 0;
        let totalSchools = 0;

        const mappedLgaStats = reportData.map((item: any) => {
          const sc = parseInt(item.schoolCount) || 0;
          const st = parseInt(item.studentCount) || 0;
          totalSchools += sc;
          totalStudents += st;

          return {
            lgaName: item.lgaName,
            lgaCode: item.lgaCode,
            schoolCount: sc,
            studentCount: st
          };
        });

        setData({
          totalStudents: totalStudents,
          totalSchools: totalSchools,
          registrationsToday: 0, // Fallback since getLgaReport doesn't provide this
          registrationStatus: 1, // Fallback 
          studentsByLGA: mappedLgaStats
        });

        setMessage({ type: "success", text: "Dashboard data loaded via LGA Report" });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
      // =====================================================================

    } catch (error) {
      console.error("Dashboard Fetch Error:", error);
      setMessage({ type: "error", text: "Error fetching dashboard data" });
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch school details for a specific LGA using lgaCode
  const fetchSchoolDetails = async (lgaCode: string, lgaName: string) => {
    console.log("Fetching schools for LGA Code:", lgaCode);

    setSchoolsLoading(true);
    try {
      // Real API call to the endpoint
      const response = await axios.post(`${baseUrl()}Admin2s/schoolsByLGA`, { lgaCode }, config);

      console.log("Schools API Response:", response.data);

      if (response.data.status) {
        // Based on your sample, response.data.data is an array of schools
        const schoolsArray = response.data.data || [];
        setSchoolDetails(schoolsArray);

        // Calculate summary statistics from the schools array
        const totalSchools = schoolsArray.length;
        const totalStudents = schoolsArray.reduce((sum: number, school: SchoolDetail) =>
          sum + (Number(school.totalStudents) || 0), 0);
        const totalMale = schoolsArray.reduce((sum: number, school: SchoolDetail) =>
          sum + (Number(school.totalMale) || 0), 0);
        const totalFemale = schoolsArray.reduce((sum: number, school: SchoolDetail) =>
          sum + (Number(school.totalFemale) || 0), 0);

        setSchoolsSummary({
          totalSchools,
          totalStudents,
          totalMale,
          totalFemale,
        });
      } else {
        // Handle empty response
        setSchoolDetails([]);
        setSchoolsSummary({
          totalSchools: 0,
          totalStudents: 0,
          totalMale: 0,
          totalFemale: 0,
        });
      }
    } catch (error) {
      console.error("Error fetching school details:", error);
      setMessage({ type: "error", text: "Error loading school details" });
      setSchoolDetails([]);
    } finally {
      setSchoolsLoading(false);
    }
  };

  const handleViewLGAClick = (lgaName: string, lgaCode?: string) => {
    // Use lgaCode if available, otherwise fallback to lgaName
    const code = lgaCode || lgaName;
    setSelectedLGA({ name: lgaName, code: code });
    fetchSchoolDetails(code, lgaName);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedLGA({ name: "", code: "" });
    setSchoolDetails([]);
    setSchoolsSummary({
      totalSchools: 0,
      totalStudents: 0,
      totalMale: 0,
      totalFemale: 0,
    });
  };

  const handleMenuClick = (menu: string) => {
    //setActiveMenu(menu);

    history.push("/" + menu);
  };

  const navigateTo = (path: string) => {
    history.push(path);
  };

  const handleLogout = () => {
    logOutUser();
    history.push("/login");
  };

  const handleBackToDashboard = () => {
    setActiveMenu("dashboard");
  };

  const handleRefresh = () => {
    fetchDashboardInfo();
  };

  const handleExportCSV = async () => {
    try {
      setMessage({ type: "success", text: "Fetching report data for CSV export..." });
      // Ensure we have access to config
      const response = await axios.post(`${baseUrl()}Admin2s/getLgaReport`, {}, config);

      if (response.data.status && response.data.data) {
        const reportData = response.data.data;

        if (!reportData || reportData.length === 0) {
          setMessage({ type: "error", text: "No data available to export." });
          return;
        }

        const headers = ["LGA Name", "LGA Code", "Local Govt Code", "Total Schools", "Total Students"];

        const rows = reportData.map((item: any) => [
          item.lgaName || "",
          item.lgaCode || "",
          item.lcode || "",
          item.schoolCount || "0",
          item.studentCount || "0"
        ]);

        const escapeCSV = (value: any) => {
          if (value === null || value === undefined) return '""';
          const str = String(value);
          return `"${str.replace(/"/g, '""')}"`;
        };

        const csvContent = [headers.map(escapeCSV), ...rows.map((row: any) => row.map(escapeCSV))].map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `State_LGA_Report_${new Date().getTime()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        setMessage({ type: "success", text: "CSV exported successfully." });
        setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      } else {
        setMessage({ type: "error", text: "Failed to fetch report data." });
      }
    } catch (error) {
      console.error("Export Error:", error);
      setMessage({ type: "error", text: "An error occurred while exporting CSV." });
    }
  };

  const handleExportPDF = async () => {
    try {
      setMessage({ type: "success", text: "Fetching report data for PDF export..." });
      const response = await axios.post(`${baseUrl()}Admin2s/getLgaReport`, {}, config);

      if (response.data.status && response.data.data) {
        const reportData = response.data.data;
        if (!reportData || reportData.length === 0) {
          setMessage({ type: "error", text: "No data available to export." });
          return;
        }

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;

        // Header
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("Ministry of Primary Education", pageWidth / 2, 20, { align: "center" });

        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.text("Admin Dashboard - State Registration Report", pageWidth / 2, 28, { align: "center" });

        // Date
        const dateStr = new Date().toLocaleDateString();
        doc.setFontSize(10);
        doc.text(`Report Generated: ${dateStr}`, 14, 40);

        // Summaries
        let totalSchools = 0;
        let totalStudents = 0;

        const tableData = reportData.map((item: any, index: number) => {
          const schools = parseInt(item.schoolCount) || 0;
          const students = parseInt(item.studentCount) || 0;
          totalSchools += schools;
          totalStudents += students;

          return [
            index + 1,
            item.lgaName || "",
            schools.toLocaleString(),
            students.toLocaleString()
          ];
        });

        // Summary Block
        doc.setFont("helvetica", "bold");
        doc.text("TOTAL STUDENTS", pageWidth / 4, 55, { align: "center" });
        doc.text("TOTAL SCHOOLS", (pageWidth / 4) * 3, 55, { align: "center" });

        doc.setFontSize(14);
        doc.text(totalStudents.toLocaleString(), pageWidth / 4, 65, { align: "center" });
        doc.text(totalSchools.toLocaleString(), (pageWidth / 4) * 3, 65, { align: "center" });

        tableData.push([
          "",
          "SUM TOTAL (ALL LGAs)",
          totalSchools.toLocaleString(),
          totalStudents.toLocaleString()
        ]);

        autoTable(doc, {
          startY: 75,
          head: [["S/N", "LGA Name", "Total Schools", "Total Students"]],
          body: tableData,
          theme: 'grid',
          headStyles: { fillColor: [36, 59, 90], textColor: 255, fontStyle: 'bold' },
          willDrawCell: function (data) {
            // Make the last row bold
            if (data.row.index === tableData.length - 1) {
              doc.setFont("helvetica", "bold");
            }
          },
          didDrawPage: function (data) {
            // Footer
            const str = "Delta State Government | Page " + doc.internal.getNumberOfPages();
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            const pageSize = doc.internal.pageSize;
            const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
            doc.text(str, data.settings.margin.left, pageHeight - 10);
          }
        });

        doc.save(`State_LGA_Report_${new Date().getTime()}.pdf`);

        setMessage({ type: "success", text: "PDF exported successfully." });
        setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      } else {
        setMessage({ type: "error", text: "Failed to fetch report data." });
      }
    } catch (error) {
      console.error("Export Error:", error);
      setMessage({ type: "error", text: "An error occurred while exporting PDF." });
    }
  };

  const handleExportLGAPDF = () => {
    try {
      if (!schoolDetails || schoolDetails.length === 0) {
        setMessage({ type: "error", text: "No school details to export." });
        return;
      }

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;

      // Header
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Ministry of Primary Education", pageWidth / 2, 20, { align: "center" });

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("LGA Analysis Report", pageWidth / 2, 28, { align: "center" });

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text(`Schools in ${selectedLGA.name} Local Government Area`, pageWidth / 2, 35, { align: "center" });

      // Date
      const dateStr = new Date().toLocaleDateString();
      doc.setFontSize(10);
      doc.text(`Generated report. Date: ${dateStr}`, 14, 45);

      // Summaries block
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("TOTAL SCHOOLS", pageWidth / 8, 55, { align: "center" });
      doc.text("TOTAL STUDENTS", (pageWidth / 8) * 3, 55, { align: "center" });
      doc.text("TOTAL MALE", (pageWidth / 8) * 5, 55, { align: "center" });
      doc.text("TOTAL FEMALE", (pageWidth / 8) * 7, 55, { align: "center" });

      doc.setFontSize(12);
      doc.text((schoolsSummary.totalSchools || 0).toString(), pageWidth / 8, 63, { align: "center" });
      doc.text((schoolsSummary.totalStudents || 0).toLocaleString(), (pageWidth / 8) * 3, 63, { align: "center" });
      doc.text((schoolsSummary.totalMale || 0).toLocaleString(), (pageWidth / 8) * 5, 63, { align: "center" });
      doc.text((schoolsSummary.totalFemale || 0).toLocaleString(), (pageWidth / 8) * 7, 63, { align: "center" });

      const tableData = schoolDetails.map((school, index) => [
        index + 1,
        school.schoolCode || "N/A",
        school.schoolName || "Unknown School",
        (Number(school.totalStudents) || 0).toLocaleString(),
        (Number(school.totalMale) || 0).toLocaleString(),
        (Number(school.totalFemale) || 0).toLocaleString()
      ]);

      autoTable(doc, {
        startY: 73,
        head: [["S/N", "School Code", "School Name", "Total Students", "Male", "Female"]],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [36, 59, 90], textColor: 255, fontStyle: 'bold' },
        didDrawPage: function (data) {
          // Footer
          const str = "Delta State Government | Page " + doc.internal.getNumberOfPages();
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
          doc.text(str, data.settings.margin.left, pageHeight - 10);
        }
      });

      doc.save(`${selectedLGA.name}_LGA_Report_${new Date().getTime()}.pdf`);
    } catch (error) {
      console.error("LGA Export PDF Error:", error);
      setMessage({ type: "error", text: "An error occurred while exporting LGA PDF." });
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setLgaFilter("All LGAs");
    setStatusFilter("All Status");
  };

  const getRegStatusDetails = (status: number | string | undefined) => {
    // status from getSettings: 1 = OPEN, 2 = CLOSED
    if (status === 1 || status === "1") {
      return { label: "OPEN", color: "success" as const };
    } else if (status === 2 || status === "2") {
      return { label: "CLOSED", color: "error" as const };
    }
    return { label: "Unknown", color: "default" as const };
  };

  const regStatus = getRegStatusDetails(systemSettings?.status);

  // Filter LGAs based on search
  const filteredLGAs = data?.studentsByLGA?.filter(lga =>
    lga.lgaName.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Safe formatting function for numbers
  const formatNumber = (value: string | number | undefined): string => {
    if (value === undefined || value === null) return '0';
    const num = Number(value);
    return isNaN(num) ? '0' : num.toLocaleString();
  };

  // Render active component based on menu selection
  const renderActiveComponent = () => {
    switch (activeMenu) {
      case "dashboard":
        return (
          <>
            {/* Dashboard Overview Text */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Monitoring student registration data and performance metrics across all LGAs.
            </Typography>

            {/* Stats Cards - Exactly matching ValidationListPage style */}
            <StatsGrid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard>
                  <StatLabel>TOTAL STUDENTS</StatLabel>
                  <StatValue>{formatNumber(data?.totalStudents)}</StatValue>
                </StatCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard>
                  <StatLabel>TOTAL SCHOOLS</StatLabel>
                  <StatValue>{formatNumber(data?.totalSchools)}</StatValue>
                </StatCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard>
                  <StatLabel>TODAY'S REGISTRATIONS</StatLabel>
                  <StatValue>{formatNumber(data?.registrationsToday)}</StatValue>
                </StatCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard>
                  <StatLabel>REGISTRATION STATUS</StatLabel>
                  <StatusBox>
                    <StatusActive>{regStatus.label}</StatusActive>
                    <StatusOnline>Portal Active</StatusOnline>
                  </StatusBox>
                </StatCard>
              </Grid>
            </StatsGrid>

            {/* Message Alert */}
            {message.text && (
              <Alert
                severity={message.type === 'success' ? 'success' : 'error'}
                sx={{ mb: 3 }}
                onClose={() => setMessage({ type: '', text: '' })}
              >
                {message.text}
              </Alert>
            )}

            {/* Search and Filter Bar - Exactly matching ValidationListPage */}
            <FilterBar>
              <SearchField
                size="small"
                placeholder="Search by LGA name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#9ca3af", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />

              <FilterSelect size="small">
                <Select
                  value={lgaFilter}
                  onChange={(e) => setLgaFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="All LGAs">All LGAs</MenuItem>
                </Select>
              </FilterSelect>

              <FilterSelect size="small">
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="All Status">All Status</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FilterSelect>

              <ActionButton
                size="small"
                onClick={handleClearFilters}
                startIcon={<FilterList />}
              >
                Clear Filters
              </ActionButton>

              <ActionButton
                size="small"
                onClick={handleExportCSV}
                startIcon={<GetApp />}
              >
                Export CSV
              </ActionButton>

              <ActionButton
                size="small"
                onClick={handleExportPDF}
                startIcon={<PictureAsPdf />}
              >
                Export PDF
              </ActionButton>

              <IconActionButton
                size="small"
                onClick={handleRefresh}
              >
                <Refresh />
              </IconActionButton>
            </FilterBar>

            {/* Table Header Info */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredLGAs.length} of {data?.studentsByLGA?.length || 0} LGAs
              </Typography>
            </Box>

            {/* LGAs Table - Tabular format like ValidationListPage */}
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <StyledTableContainer component={Paper}>
                <Table>
                  <StyledTableHead>
                    <TableRow>
                      <TableCell><b>S/N</b></TableCell>
                      <TableCell><b>LGA NAME</b></TableCell>
                      <TableCell align="center"><b>STUDENTS</b></TableCell>
                      <TableCell align="center"><b>SCHOOLS</b></TableCell>
                      <TableCell align="center"><b>AVG STUDENTS/SCHOOL</b></TableCell>
                      <TableCell align="center"><b>STATUS</b></TableCell>
                      <TableCell align="center"><b>ACTION</b></TableCell>
                    </TableRow>
                  </StyledTableHead>
                  <TableBody>
                    {filteredLGAs.map((lga, index) => (
                      <StyledTableRow key={lga.lgaName}>
                        <StyledTableCell>{index + 1}</StyledTableCell>
                        <StyledTableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: "#243b5a" }}>
                              <LocationCity sx={{ fontSize: 16 }} />
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {lga.lgaName}
                            </Typography>
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatNumber(lga.studentCount)}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {lga.schoolCount || 0}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <Chip
                            label={lga.schoolCount && lga.schoolCount > 0 ? (Number(lga.studentCount) / lga.schoolCount).toFixed(1) : "0"}
                            size="small"
                            sx={{
                              bgcolor: "#e3f2fd",
                              color: "#2196f3",
                              fontWeight: 600,
                              minWidth: 50
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <Chip
                            label={Number(lga.studentCount) > 0 ? "Active" : "Inactive"}
                            size="small"
                            sx={{
                              bgcolor: Number(lga.studentCount) > 0 ? "#10b981" : "#9ca3af",
                              color: "white",
                              minWidth: 60,
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <ViewButton
                            size="small"
                            onClick={() => handleViewLGAClick(lga.lgaName, lga.lgaCode)}
                            startIcon={<Visibility />}
                          >
                            View
                          </ViewButton>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}

                    {filteredLGAs.length === 0 && !loading && (
                      <StyledTableRow>
                        <StyledTableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            No LGA data found
                          </Typography>
                        </StyledTableCell>
                      </StyledTableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Pagination Info */}
                <Box sx={{ p: 2, borderTop: "1px solid #e5e7eb", bgcolor: "#fafafa" }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing 1 to {filteredLGAs.length} of {data?.studentsByLGA?.length || 0} LGAs
                  </Typography>
                </Box>
              </StyledTableContainer>
            )}
          </>
        );
      case "students":
        return <StudentsView />;
      case "schools":
        return <SchoolsView />;
      case "settings":
        return <SettingsView />;
      case "management":
        return <ManagementConsoleView />;
      case "domain1":
        return <DomainOneView />;
      case "domain2":
        return <DomainTwoView />;
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <IonPage>
        <IonContent>
          <Box sx={{
            bgcolor: "background.default",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}>
            {/* HEADER - Matching ValidationListPage */}
            <Header>
              <HeaderTopRow>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LogoCircle>⚖️</LogoCircle>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#cbd5e1", letterSpacing: "0.5px", display: "block" }}>
                      DELTA STATE GOVERNMENT
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 600, lineHeight: 1.2 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Admin Dashboard
                    </Typography>
                  </Box>
                </Box>

                <SchoolInfoBox>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: "#ffffff", textTransform: "uppercase" }}
                  >
                    {userData.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                    LGA: {userData.lgaCode} | Code: {userData.schoolCode}
                  </Typography>
                </SchoolInfoBox>
              </HeaderTopRow>

              <HeaderBottomRow>
                <LogoutButton
                  variant="contained"
                  onClick={handleLogout}
                  startIcon={<Logout />}
                >
                  Logout
                </LogoutButton>
              </HeaderBottomRow>

              {/* Navigation Menu - Six Items */}
              <NavMenu>
                <NavButton
                  active={activeMenu === "admin_dashboard"}
                  onClick={() => handleMenuClick("admin_dashboard")}
                  startIcon={<Dashboard />}
                >
                  Admin Dashboard
                </NavButton>
                <NavButton
                  active={activeMenu === "admin_students"}
                  onClick={() => handleMenuClick("admin_students")}
                  startIcon={<People />}
                >
                  Students
                </NavButton>
                <NavButton
                  active={activeMenu === "lga_student_database"}
                  onClick={() => handleMenuClick("lga_student_database")}
                  startIcon={<Group />}
                >
                  LGA Student Database
                </NavButton>
                <NavButton
                  active={activeMenu === "admin_schools"}
                  onClick={() => handleMenuClick("admin_schools")}
                  startIcon={<School />}
                >
                  View Schools
                </NavButton>
                <NavButton
                  active={activeMenu === "add_result"}
                  onClick={() => handleMenuClick("add_result")}
                  startIcon={<HowToReg />}
                >
                  Add Result
                </NavButton>
                <NavButton
                  active={activeMenu === "admin_settings"}
                  onClick={() => handleMenuClick("admin_settings")}
                  startIcon={<Settings />}
                >
                  Settings
                </NavButton>
                <NavButton
                  active={activeMenu === "admin_management"}
                  onClick={() => handleMenuClick("admin_management")}
                  startIcon={<AdminPanelSettings />}
                >
                  Management Console
                </NavButton>

                <NavButton
                  active={activeMenu === "admin_stats"}
                  onClick={() => handleMenuClick("admin_stats")}
                  startIcon={<Assessment />}
                >
                  Activity Stats
                </NavButton>

              </NavMenu>
            </Header>


            {/* MAIN CONTENT */}
            <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
              {/* Back to Dashboard Button (only show if not on dashboard) */}
              {activeMenu !== "dashboard" && (
                <Button
                  onClick={handleBackToDashboard}
                  startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                  sx={{
                    color: "#243b5a",
                    fontSize: "13px",
                    textTransform: "none",
                    p: 0,
                    mb: 3,
                    '&:hover': {
                      textDecoration: "underline",
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  Back to Dashboard
                </Button>
              )}

              {/* Render active component */}
              {renderActiveComponent()}
            </Container>

            {/* FOOTER - Exactly matching ValidationListPage */}
            <Box component="footer" sx={{ bgcolor: "primary.main", color: "#d1d5db", py: { xs: 5, md: 6 }, mt: "auto" }}>
              <Container maxWidth="lg">
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      MINISTRY OF PRIMARY EDUCATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Delta State Government
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Asaba, Delta State, Nigeria
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      CONTACT INFORMATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Email: info@dsgmope.ng
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Address: Ministry Secretariat, Asaba
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Telephone: +234 (0)901 234 5678
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      OFFICIAL RESOURCES
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <FooterLink href="#">www.dsgmope.ng</FooterLink>
                    </Typography>
                    <Typography variant="body2">
                      <FooterLink href="#">Delta State Government Portal</FooterLink>
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 3 }} />

                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", maxWidth: 900, mx: "auto", mb: 2 }}
                >
                  OFFICIAL NOTICE: This is an official portal of the Delta State Government, Ministry of Primary Education. Unauthorized access, misuse, or tampering with this system is prohibited and may be subject to legal action. All information provided is for official purposes only.
                </Typography>

                <Typography variant="body2" align="center" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}>
                  © 2026 Delta State Government, Ministry of Primary Education. All rights reserved.
                </Typography>
              </Container>
            </Box>
          </Box>

          {/* School Details Dialog - Updated to match actual API response */}
          <Dialog
            open={dialogOpen}
            onClose={handleCloseDialog}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: "8px",
                maxHeight: "90vh",
              }
            }}
          >
            <DialogHeader>
              <DialogTitleStyled>
                Schools in {selectedLGA.name} Local Government Area
              </DialogTitleStyled>
              <IconButton onClick={handleCloseDialog} size="small">
                <CloseIcon />
              </IconButton>
            </DialogHeader>

            <DialogContent sx={{ p: 3 }}>
              {schoolsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {/* Summary Stats Cards */}
                  <SchoolStatsGrid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <SchoolStatCard>
                        <SchoolStatValue>{schoolsSummary.totalSchools || 0}</SchoolStatValue>
                        <SchoolStatLabel>Total Schools</SchoolStatLabel>
                      </SchoolStatCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <SchoolStatCard>
                        <SchoolStatValue>{formatNumber(schoolsSummary.totalStudents)}</SchoolStatValue>
                        <SchoolStatLabel>Total Students</SchoolStatLabel>
                      </SchoolStatCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <SchoolStatCard>
                        <SchoolStatValue>{formatNumber(schoolsSummary.totalMale)}</SchoolStatValue>
                        <SchoolStatLabel>Total Male</SchoolStatLabel>
                      </SchoolStatCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <SchoolStatCard>
                        <SchoolStatValue>{formatNumber(schoolsSummary.totalFemale)}</SchoolStatValue>
                        <SchoolStatLabel>Total Female</SchoolStatLabel>
                      </SchoolStatCard>
                    </Grid>
                  </SchoolStatsGrid>

                  {/* Schools Table */}
                  <TableContainer component={Paper} sx={{ border: "1px solid #e5e7eb" }}>
                    <Table>
                      <StyledTableHead>
                        <TableRow>
                          <TableCell><b>S/N</b></TableCell>
                          <TableCell><b>SCHOOL CODE</b></TableCell>
                          <TableCell><b>SCHOOL NAME</b></TableCell>
                          <TableCell align="center"><b>TOTAL STUDENTS</b></TableCell>
                          <TableCell align="center"><b>MALE</b></TableCell>
                          <TableCell align="center"><b>FEMALE</b></TableCell>
                        </TableRow>
                      </StyledTableHead>
                      <TableBody>
                        {schoolDetails && schoolDetails.length > 0 ? (
                          schoolDetails.map((school, index) => (
                            <StyledTableRow key={school.id || index}>
                              <StyledTableCell>{index + 1}</StyledTableCell>
                              <StyledTableCell>
                                <Chip
                                  label={school.schoolCode || 'N/A'}
                                  size="small"
                                  sx={{ bgcolor: "#e3f2fd", color: "#243b5a", fontWeight: 600 }}
                                />
                              </StyledTableCell>
                              <StyledTableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Avatar sx={{ width: 32, height: 32, bgcolor: "#e3f2fd", color: "#243b5a" }}>
                                    <School sx={{ fontSize: 16 }} />
                                  </Avatar>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {school.schoolName || 'Unknown School'}
                                  </Typography>
                                </Box>
                              </StyledTableCell>
                              <StyledTableCell align="center">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {formatNumber(school.totalStudents)}
                                </Typography>
                              </StyledTableCell>
                              <StyledTableCell align="center">
                                <Chip
                                  icon={<Male sx={{ fontSize: 14 }} />}
                                  label={formatNumber(school.totalMale)}
                                  size="small"
                                  sx={{ bgcolor: "#e3f2fd", color: "#1976d2" }}
                                />
                              </StyledTableCell>
                              <StyledTableCell align="center">
                                <Chip
                                  icon={<Female sx={{ fontSize: 14 }} />}
                                  label={formatNumber(school.totalFemale)}
                                  size="small"
                                  sx={{ bgcolor: "#fce4ec", color: "#c2185b" }}
                                />
                              </StyledTableCell>
                            </StyledTableRow>
                          ))
                        ) : (
                          <StyledTableRow>
                            <StyledTableCell colSpan={6} align="center" sx={{ py: 4 }}>
                              <Typography color="text.secondary">
                                No schools found for this LGA
                              </Typography>
                            </StyledTableCell>
                          </StyledTableRow>
                        )}
                      </TableBody>
                    </Table>

                    {/* Pagination Info */}
                    {schoolDetails && schoolDetails.length > 0 && (
                      <Box sx={{ p: 2, borderTop: "1px solid #e5e7eb", bgcolor: "#fafafa" }}>
                        <Typography variant="body2" color="text.secondary">
                          Showing 1 to {schoolDetails.length} of {schoolDetails.length} schools
                        </Typography>
                      </Box>
                    )}
                  </TableContainer>
                </>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: "1px solid #e5e7eb" }}>
              <Button
                onClick={handleExportLGAPDF}
                variant="outlined"
                startIcon={<PictureAsPdf />}
                sx={{ mr: 1, borderColor: "#243b5a", color: "#243b5a", '&:hover': { borderColor: "#1c2e47", backgroundColor: "rgba(36, 59, 90, 0.04)" } }}
                disabled={!schoolDetails || schoolDetails.length === 0}
              >
                Export PDF
              </Button>
              <Button onClick={handleCloseDialog} variant="contained" sx={{ bgcolor: "#243b5a" }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </IonContent>
      </IonPage>
    </ThemeProvider>
  );
};

export default AdminDashboard;